import React, { useState } from 'react';
import type { Venue, RedemptionCode } from '../../../types/venue';
import { lookupRedemption, redeemCode } from '../../../utils/venueApi';
import Button from '../../ui/Button';

interface RedemptionPanelProps {
  venue: Venue;
}

const RedemptionPanel: React.FC<RedemptionPanelProps> = ({ venue }) => {
  const [code, setCode] = useState('');
  const [result, setResult] = useState<RedemptionCode | null>(null);
  const [searching, setSearching] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [staffNote, setStaffNote] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleSearch = async () => {
    if (!code.trim()) return;
    setSearching(true);
    setFeedback(null);
    setResult(null);
    try {
      const found = await lookupRedemption(venue.id, code.trim().toUpperCase());
      setResult(found);
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message?.includes('404') ? 'Code not found.' : (err.message || 'Lookup failed.') });
    } finally {
      setSearching(false);
    }
  };

  const handleRedeem = async () => {
    if (!result) return;
    setRedeeming(true);
    setFeedback(null);
    try {
      const updated = await redeemCode(venue.id, result.code);
      setResult(updated);
      setFeedback({ type: 'success', message: 'Code redeemed.' });
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message || 'Redemption failed.' });
    } finally {
      setRedeeming(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow mb-1.5">Redemption</div>
        <h2 className="text-display text-2xl text-coffee-900">Redemption lookup</h2>
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Enter redemption code"
          maxLength={8}
          className="flex-1 px-3 py-2.5 border border-coffee-200 rounded-lg text-sm font-mono uppercase tracking-wider text-coffee-900 bg-white focus:outline-none focus:ring-2 focus:ring-coffee-500 transition-colors"
          aria-label="Redemption code"
        />
        <Button
          variant="primary"
          onClick={handleSearch}
          disabled={searching || !code.trim()}
          loading={searching}
        >
          {searching ? 'Looking up...' : 'Look up'}
        </Button>
      </div>

      {result && (
        <div className="bg-white border border-coffee-100 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <span className="font-mono text-xl font-bold text-coffee-900">{result.code}</span>
            <span className={`px-2.5 py-0.5 text-xs tracking-eyebrow font-semibold rounded-full ${
              result.redeemedAt
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-amber-50 text-amber-800 border border-amber-200'
            }`}>
              {result.redeemedAt ? 'Redeemed' : 'Pending'}
            </span>
          </div>

          <div className="text-sm space-y-1.5 border-t border-coffee-100 pt-4">
            <p className="text-coffee-700">
              <span className="text-coffee-500 text-xs tracking-eyebrow font-semibold">Reward</span>
              <br />{result.rewardText}
            </p>
            <p className="text-coffee-700">
              <span className="text-coffee-500 text-xs tracking-eyebrow font-semibold">Issued</span>
              <br />{new Date(result.issuedAt).toLocaleDateString()}
            </p>
            {result.redeemedAt && (
              <p className="text-coffee-700">
                <span className="text-coffee-500 text-xs tracking-eyebrow font-semibold">Redeemed</span>
                <br />{new Date(result.redeemedAt).toLocaleDateString()}
              </p>
            )}
            {result.staffNote && (
              <p className="text-coffee-700">
                <span className="text-coffee-500 text-xs tracking-eyebrow font-semibold">Note</span>
                <br />{result.staffNote}
              </p>
            )}
          </div>

          {!result.redeemedAt && (
            <div className="space-y-2 pt-2 border-t border-coffee-100">
              <input
                type="text"
                value={staffNote}
                onChange={(e) => setStaffNote(e.target.value)}
                placeholder="Staff note (optional)"
                className="w-full px-3 py-2.5 border border-coffee-200 rounded-lg text-sm text-coffee-900 bg-white focus:outline-none focus:ring-2 focus:ring-coffee-500 transition-colors"
              />
              <Button
                variant="primary"
                fullWidth
                onClick={handleRedeem}
                loading={redeeming}
                disabled={redeeming}
              >
                {redeeming ? 'Redeeming...' : 'Mark as redeemed'}
              </Button>
            </div>
          )}
        </div>
      )}

      {feedback && (
        <div className={`text-sm rounded-xl p-4 border ${
          feedback.type === 'success'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-red-50 text-red-700 border-red-200'
        }`}>
          {feedback.message}
        </div>
      )}
    </div>
  );
};

export default RedemptionPanel;
